<template>
  <template v-if="restricted !== RESTRICTION.NOT_READY">
    <site-is-not-available-in-this-region v-if="restricted === RESTRICTION.SERVICE_NOT_ALLOWED" />
    <technical-work v-else-if="showUnderDev" />
    <media-query-provider v-else :queries="$options.queries">
      <router-view />
      <custom-modal v-if="appModal.isActive" v-model="appModal.isActive" v-bind="appModal.props" />
    </media-query-provider>
  </template>
</template>

<script>
// vue
import { computed } from 'vue'
import * as Sentry from '@sentry/vue'
// configs
import { BASE_URL, LIST_OF_GET_PARAMS_TO_SAVE, LOCATIONS_LIST } from 'src/common/config'
// services
import { dga } from 'src/common/websocket-dga'
// webview routes
import { webviewRoutesArray } from 'src/router/webview-routes'
// utils
import { defineApp, externalLinkMessageToApp } from 'src/common/webview'
import { useMetadata } from 'src/hooks/useMetadata'
import { consoleLogAPI, getModal, preloaderVisible } from 'src/common/functions'
import { RESTRICTION, UNDER_DEV } from 'src/common/dictionary'
// queries
import getCurrencyList from 'src/apollo/queries/getCurrencyList.gql'
import getContentList from 'src/apollo/queries/getContentList.gql'
import getContent from 'src/apollo/queries/getContent.gql'
// components
import MediaQueryProvider from 'components/MediaQueryProvider.vue'
import CustomModal from 'src/core/components/customModal/CustomModal.vue'
import SiteIsNotAvailableInThisRegion from 'components/technical/SiteIsNotAvailableInThisRegion.vue'

import 'swiper/css'
import 'swiper/css/grid'
import 'swiper/css/free-mode'
import '@gamesite/olimp-core/styles/blue-theme.css'
import '@gamesite/olimp-core/styles/core.css'
import '@gamesite/olimp-core/styles/index.scss'
import FingerPrint from '@gamesite/fingerprint/dist/index'
import userPromo from 'src/apollo/queries/userPromo.gql'
import { getConfig } from 'src/configs'
import { mapActions } from 'pinia'
import { usePromoStore } from 'src/stores/promo'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useSystemStore } from 'src/stores/system'
import { useUserBonusesStore } from 'src/stores/user/bonuses'
import { useUserAuthStore } from 'src/stores/user/auth'
import { useMainStore } from 'src/stores/main'
import { useHandleQueries } from 'src/hooks/query'
import TechnicalWork from 'components/technical/TechnicalWork.vue'
import { useServerStatusStore } from 'stores/server-status'
import { usePollingUpdate } from 'src/hooks/usePollingUpdate'

import(`../src/css/${process.env.PLATFORM}.variables.scss`)

const queries = {
  mobileNano: '(max-width: 360px)',
  mobileSmall: '(max-width: 575px)',
  mobile: '(max-width: 768px)',
  tab: '(min-width: 768px)',
  desktop: '(min-width: 1024px)',
  desktopMedium: '(min-width: 1200px)',
  desktopBig: '(min-width: 1440px)',
}

const config = getConfig()

export default {
  name: 'App',
  components: { TechnicalWork, SiteIsNotAvailableInThisRegion, MediaQueryProvider, CustomModal },
  queries,
  provide() {
    return {
      queries,
      appModal: this.appModal,
      dwh: this.dwhSendEvent,
    }
  },
  setup() {
    const systemStore = useSystemStore()
    const serverStatusStore = useServerStatusStore()
    const restricted = computed(() => serverStatusStore.restricted)
    const showUnderDev = computed(
      () => serverStatusStore.underDev === UNDER_DEV.ACTIVE || !serverStatusStore.serverIsOk
    )

    if (restricted.value === RESTRICTION.SERVICE_NOT_ALLOWED || showUnderDev.value) {
      preloaderVisible(false)
      return { restricted, showUnderDev }
    }

    useHandleQueries()
    usePollingUpdate()
    useMetadata()

    const isProd = process.env.NODE_ENV === 'production' && process.env.ENVIRONMENT === 'production'
    const isBot = systemStore.isBot

    if (isProd && !isBot) {
      const waitForGtm = (timeout = 7000) => {
        return new Promise((resolve) => {
          const start = Date.now()

          const checkGtmReady = () => {
            const elapsed = Date.now() - start

            if (window.dataLayer && window.dataLayer.some((event) => event.event === 'gtm.js')) {
              resolve(true)
            } else if (elapsed >= timeout) {
              resolve(false)
            } else {
              setTimeout(checkGtmReady, 1000)
            }
          }

          checkGtmReady()
        })
      }

      waitForGtm().then(() => {
        try {
          new FingerPrint(`${BASE_URL}/fp`).init(useUserAuthStore().getUser?.id, localStorage.getItem('a_ssid'))
        } catch (e) {
          console.error(e)
        }
      })
    }

    return { restricted, showUnderDev }
  },
  data() {
    return {
      appModal: {
        isActive: false,
        props: null,
        dwhError: (event) => {
          this.dwhSendEvent(event)
        },
        open: function (props) {
          this.isActive = true
          return new Promise((resolve, reject) => {
            this.props = { ...props, actions: props.actions, resolve, reject }
          })
            .catch(() => {
              if (props.props?.tab === 'login') {
                this.dwhError('click_close_auth_form')
              } else if (props.props?.tab === 'reg') {
                this.dwhError('click_close_reg_form')
              }
            })
            .finally(() => {
              this.props = null
            })
        },
        close: function () {
          this.isActive = false
        },
      },
    }
  },
  computed: {
    RESTRICTION() {
      return RESTRICTION
    },
    getAuth() {
      return useUserAuthStore().getAuth
    },
    getUser() {
      return useUserAuthStore().getUser
    },
    isLiveChatPage() {
      return this.$route.name === 'livechat'
    },
    isWebViewPage() {
      return webviewRoutesArray.map((item) => item.name).includes(this.$route.name)
    },
  },
  watch: {
    '$i18n.locale': {
      handler(val) {
        document.documentElement.setAttribute('lang', LOCATIONS_LIST[val] || val)
      },
      immediate: true,
    },
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (newQuery.modal && newQuery.modal !== oldQuery.modal) {
          this.openQueryModal(newQuery.modal)
        }
      },
      deep: true,
    },
  },
  beforeCreate() {
    try {
      Sentry.setTag('platform', process.env.PLATFORM)
      Sentry.setTag('webview', process.env.WEBVIEW === '1')
    } catch {
      console.warn('Cannot set tags to sentry')
    }
  },
  mounted() {
    this.getIsShowBigWins()

    if (this.isWebViewPage || this.restricted) {
      return
    }

    useMainStore().updateMainGroups()

    this.setGtmUser()

    this.$router
      .isReady()
      .then(() => {
        if (this.isLiveChatPage) {
          return
        }

        if (this.$route.query.promoreg) {
          localStorage.setItem(
            'promoreg',
            JSON.stringify({
              promoreg: this.$route.query.promoreg,
              date: Date.now(),
            })
          )
        }

        if (this.$route.query.cinn) {
          localStorage.setItem('cinn', '1')
        }
        if (this.$route.query.v) {
          localStorage.setItem('appVersion', this.$route.query.v)
        }
        if (!localStorage.getItem('u_lang') && this.$route.params.locale) {
          localStorage.setItem('u_lang', this.$route.params.locale.toUpperCase())
        }

        this.initWsDGA()
        this.fetchCurrencyList()
        this.fetchPromoTypes()
        if (this.getAuth) {
          this.fetchPromoList()

          if (this.$route.hash === '#payment_decline' && this.$route.query.old_money_order_uuid) {
            const oldMoneyOrderUuid = this.$route.query.old_money_order_uuid
            this.openQueryModal('repeatTopUpModal', { oldMoneyOrderUuid })
          }
        }
        if (this.$route.query.modal) {
          this.$axios.dwh
            .sendEvent('registration_initiated_modal', useSiteInfoStore().getCountryCode)
            .catch(consoleLogAPI)
          this.openQueryModal(this.$route.query.modal)
        }

        window.addEventListener('message', this.listenIframe)
        if (useSiteInfoStore().isWebView) {
          window.addEventListener('click', this.handleClickToCatchExternalLinks)
        }

        this.clearQueryParams()
      })
      .catch(console.error)
  },
  beforeUnmount() {
    window.removeEventListener('message', this.listenIframe)
    window.removeEventListener('click', this.handleClickToCatchExternalLinks)
  },
  methods: {
    ...mapActions(usePromoStore, {
      setPromoTypes: 'setPromoTypes',
      setIsShowBigWins: 'setIsShowBigWins',
    }),

    setGtmUser() {
      if (!this.getUser?.id) {
        return
      }

      if (process.env.GTM) {
        this.$gtm.push({
          event: 'userLoggedIn',
          user_id: this.getUser.id,
        })
      }
    },
    getIsShowBigWins() {
      this.$apollo
        .query({
          query: getContent,
          variables: {
            type: 'isShowBigWins',
            title: process.env.PLATFORM.toLowerCase() + '_is_show_big_wins',
          },
        })
        .then((response) => {
          if (response?.data?.propsContent?.props) {
            // Отключено на время доработки топ выигрышей
            // const isShowBigWins = JSON.parse(response.data.propsContent.props)?.isShowBigWins
            // this.setIsShowBigWins(isShowBigWins)
            this.setIsShowBigWins(false)
          }
        })
    },
    dwhSendEvent(event, params = {}) {
      if (!config.useDWH) {
        return
      }

      const isIosWebview = location.hostname === 'apps-olymp.xyz'

      const origin = {
        APP_IOS: 'APP_IOS',
        APP_ANDROID: 'APP_ANDROID',
        BROWSER_MOBILE: 'BROWSER_MOBILE',
        BROWSER_DESKTOP: 'BROWSER_DESKTOP',
      }

      let device = 'UNDEFINED'

      if (defineApp() === 'map' && useSiteInfoStore().isWebView) {
        device = origin.APP_ANDROID
      } else if (defineApp() === 'mav' && useSiteInfoStore().isWebView) {
        device = origin.APP_IOS
      } else if (isIosWebview && useSiteInfoStore().isWebView) {
        device = origin.APP_IOS
      } else if (useSystemStore().isMobile && !useSiteInfoStore().isWebView) {
        device = origin.BROWSER_MOBILE
      } else if (!useSystemStore().isMobile && !useSiteInfoStore().isWebView) {
        device = origin.BROWSER_DESKTOP
      }

      this.$axios.dwh.sendEvent(event, useSiteInfoStore().getCountryCode, device, params).catch(consoleLogAPI)
    },
    handleClickToCatchExternalLinks(event) {
      const target = event.target.closest('a')
      if (target && target.href) {
        const url = new URL(target.href)
        if (url.origin !== window.location.origin) {
          event.preventDefault()
          externalLinkMessageToApp(url.toString())
        }
      }
    },
    openQueryModal(modalName) {
      const modal = getModal(modalName, this.getAuth)
      if (modal) {
        const { componentName, props } = modal
        this.appModal
          .open({ componentName: componentName(), props })
          .finally(() => this.$router.replace({ query: null }))
      }
    },
    listenIframe(event) {
      const eventData = event?.data
      if (!eventData) {
        return
      }
      if (eventData === 'geo_blocking_get_lang') {
        event.source.postMessage({ name: 'geo_blocking_lang', lang: this.$i18n.locale }, '*')
      }

      if (eventData === 'geo_blocking_back') {
        const prevRoute = useSystemStore().prevRoute?.name ? useSystemStore().prevRoute : { name: 'Home' }
        this.$router.replace(prevRoute).catch(() => {})
      }

      const arrayHiddenTypes = ['webpackInvalid', 'application/x-postmate-v1+json', 'action-hook-fired']
      const arrayHiddenMessage = ['action-hook-fired', 'reload-tab-complete']

      if (
        localStorage.getItem('show-events') &&
        !arrayHiddenTypes.includes(eventData?.type) &&
        !arrayHiddenMessage.includes(eventData?.message)
      ) {
        // eslint-disable-next-line no-console
        console.log('Event data from iframe.', eventData)
      }

      const pushEvent = eventData.push && typeof eventData.push === 'string'
      const payload = eventData.payload || eventData.value
      const actionEventGame = eventData.action === 'game.location' && payload

      if (pushEvent || actionEventGame) {
        if (localStorage.getItem('show-events')) {
          // eslint-disable-next-line no-console
          console.log('event', event)
          // eslint-disable-next-line no-console
          console.log('pushEvent', pushEvent)
          // eslint-disable-next-line no-console
          console.log('actionEventGame', actionEventGame)
        }
        let unresolvedUrl = pushEvent ? eventData.push : payload

        if (unresolvedUrl.startsWith('http')) {
          try {
            const parsedURL = new URL(unresolvedUrl)
            unresolvedUrl = parsedURL.pathname + parsedURL.search
          } catch (e) {
            console.error(e)
          }
        }

        try {
          if (BASE_URL && unresolvedUrl.startsWith(BASE_URL)) {
            unresolvedUrl = unresolvedUrl.replace(BASE_URL, '')
          }
          const newRoute = this.$router.resolve(unresolvedUrl)

          if (
            newRoute &&
            newRoute.fullPath !== this.$route.fullPath &&
            !['Auth', 'AuthEmail'].includes(newRoute.name)
          ) {
            return this.$router.replace(newRoute).catch(() => {})
          }
          this.$router.replace({ name: 'Home' }).catch(() => {})
        } catch {
          this.$router.replace({ name: 'Home' }).catch(() => {})
        }
      }
    },
    initWsDGA() {
      if (process.env.DGA_URI && process.env.DGA_CASINO_ID) {
        dga.connect(process.env.DGA_URI, process.env.DGA_CASINO_ID, useSiteInfoStore().userCurrency?.code)
      }
    },
    fetchPromoList() {
      this.$apollo
        .query({
          query: userPromo,
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          useUserBonusesStore().updateProgramAvailable(data?.userPromo?.programsAvailable ?? [])
          useUserBonusesStore().updateActiveStepLoaded()

          if (data?.userPromo?.activeStep) {
            useUserBonusesStore().updateActiveStep(data.userPromo.activeStep)
            useUserBonusesStore().updateUserProgram(data.userPromo.activeStep.userProgram)
            useUserBonusesStore().updateBonusGameIdsList(data.userPromo.activeStep.gameIdsList)
          }
        })
        .catch((e) => {
          consoleLogAPI(e)
        })
    },
    fetchCurrencyList() {
      this.$apollo
        .query({
          query: getCurrencyList,
        })
        .then(({ data }) => {
          useSiteInfoStore().setCurrencyData(data)
        })
        .catch(consoleLogAPI)
    },
    fetchPromoTypes() {
      this.$apollo
        .query({
          query: getContentList,
          variables: {
            type: 'promotion_types_v2',
          },
        })
        .then(({ data }) => {
          const { propsContentList } = data
          const promoTypes = propsContentList
            .map((promoType) => ({ id: promoType.id, ...JSON.parse(promoType.props) }))
            .sort((a, b) => a.id - b.id)

          this.setPromoTypes(promoTypes)
        })
        .catch(consoleLogAPI)
    },
    clearQueryParams() {
      const query = { ...this.$route.query }
      let hasParams = false
      for (const param in LIST_OF_GET_PARAMS_TO_SAVE) {
        if (query[param]) {
          hasParams = true
          delete query[param]
        }
      }
      if (hasParams) {
        this.$router.replace({ query })
      }
    },
  },
}
</script>

<style lang="scss">
body {
  background: var(--cas-dark-2);
}
</style>
